/**
 * Utility functions for help text processing
 */

/**
 * Replaces year references in text with the provided tax year
 * Handles formats like: 2024-25, 2024, 2025, 6 April 2024 to 5 April 2025
 */
export function replaceYearReferences(text: string, taxYear: string): string {
  if (!taxYear) return text
  
  // Extract start and end years from tax year format (e.g., "2024-25" -> 2024, 2025)
  const [startYear] = taxYear.split('-')
  if (!startYear) return text
  const startYearNum = parseInt(startYear)
  const endYearNum = startYearNum + 1
  
  // Replace common year patterns
  let result = text
  
  // Replace "2024-25" format
  result = result.replace(/2024-25/g, taxYear)
  result = result.replace(/2023-24/g, taxYear)
  
  // Replace "6 April 2024 to 5 April 2025" format
  result = result.replace(/6 April 2024 to 5 April 2025/g, `6 April ${startYearNum} to 5 April ${endYearNum}`)
  result = result.replace(/6 April 2023 to 5 April 2024/g, `6 April ${startYearNum - 1} to 5 April ${startYearNum}`)
  
  // Replace standalone years
  result = result.replace(/2024/g, startYearNum.toString())
  result = result.replace(/2025/g, endYearNum.toString())
  result = result.replace(/2023/g, (startYearNum - 1).toString())
  
  // Replace "year to 5 April 2025"
  result = result.replace(/year to 5 April 2025/g, `year to 5 April ${endYearNum}`)
  result = result.replace(/year to 5 April 2024/g, `year to 5 April ${startYearNum}`)
  
  // Replace "before 6 April 2025"
  result = result.replace(/before 6 April 2025/g, `before 6 April ${endYearNum}`)
  result = result.replace(/before 6 April 2024/g, `before 6 April ${startYearNum}`)
  
  // Replace "after 5 April 2025"
  result = result.replace(/after 5 April 2025/g, `after 5 April ${endYearNum}`)
  result = result.replace(/after 5 April 2024/g, `after 5 April ${startYearNum}`)
  
  // Replace "ended 5 April 2025"
  result = result.replace(/ended 5 April 2025/g, `ended 5 April ${endYearNum}`)
  result = result.replace(/ended 5 April 2024/g, `ended 5 April ${startYearNum}`)
  
  return result
}

/**
 * Summarizes long help text to a more concise version
 */
export function summarizeHelpText(text: string, maxLength: number = 300): string {
  if (text.length <= maxLength) return text
  
  // Try to cut at a sentence boundary
  const truncated = text.substring(0, maxLength)
  const lastPeriod = truncated.lastIndexOf('.')
  const lastNewline = truncated.lastIndexOf('\n')
  const cutPoint = Math.max(lastPeriod, lastNewline)
  
  if (cutPoint > maxLength * 0.7) {
    return truncated.substring(0, cutPoint + 1) + '...'
  }
  
  // If no good sentence boundary, cut at word boundary
  const lastSpace = truncated.lastIndexOf(' ')
  if (lastSpace > maxLength * 0.7) {
    return truncated.substring(0, lastSpace) + '...'
  }
  
  return truncated + '...'
}
